'use client';

import { ReferenceItem } from '@/app/services/llm';
import AiIdentification from '@/components/ai-identification';
import { MarkdownRenderer } from '@/components/markdown-renderer';
import { addHeaderAnchors, replaceReferenceWithLink } from '@/lib/util';
import { Loader2 } from 'lucide-react';
import { ReactNode, useCallback, useEffect, useRef } from 'react';

type DeepResearchReportProps = {
  reportContent: string;
  references: ReferenceItem[];
  writing: boolean;
  done: boolean;
  isCompleted: boolean;
  nextStep: string;
  scrollContainer: HTMLElement | null;
  onRefClick: (index: number) => void;
  footerActions?: ReactNode;
};

export function DeepResearchReport({
  reportContent,
  references,
  writing,
  done,
  isCompleted,
  nextStep,
  scrollContainer,
  onRefClick,
  footerActions,
}: DeepResearchReportProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);

  // Track user scroll position on the center panel
  useEffect(() => {
    const container = scrollContainer;
    if (!container || !writing) {
      userScrolledUpRef.current = false;
      return;
    }

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const isAtBottom = scrollHeight - scrollTop - clientHeight < 100;
      userScrolledUpRef.current = !isAtBottom;
    };

    container.addEventListener('scroll', handleScroll, {
      passive: true,
    });
    return () => container.removeEventListener('scroll', handleScroll);
  }, [writing, scrollContainer]);

  // Auto-scroll only during live writing.
  // isCompleted=true (detail.status==='DONE') means SSE replay, skip entirely.
  useEffect(() => {
    if (writing && !isCompleted && !userScrolledUpRef.current) {
      bottomRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
  }, [reportContent, writing, isCompleted]);

  // React event delegation for reference clicks
  const handleReportClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('ref-link')) {
        e.preventDefault();
        const index = Number(target.textContent) - 1;
        onRefClick(index);
      }
    },
    [onRefClick],
  );

  return (
    <div className="px-4 md:px-0 pb-8 max-w-full mx-auto md:max-w-4xl">
      <div className="space-y-3 leading-6" onClick={handleReportClick}>
        <MarkdownRenderer
          content={addHeaderAnchors(
            replaceReferenceWithLink(reportContent, references, false, true),
          )}
          allowHtml
        />
      </div>
      <div ref={bottomRef} />

      {writing && (
        <div className="flex flex-col items-center justify-center mt-5 gap-1">
          <Loader2 className="h-4 w-4 animate-spin" />
          {nextStep && (
            <span className="mt-1 text-muted-foreground text-sm">
              {nextStep}
            </span>
          )}
        </div>
      )}

      {done && footerActions ? (
        <div className="mt-8 border-t border-border/50 pt-4">
          {footerActions}
        </div>
      ) : null}

      {done && (
        <div className="flex justify-center pt-8">
          <AiIdentification />
        </div>
      )}
    </div>
  );
}
